import {useLayoutEffect, useRef} from 'react';

const MAX_LINES = 2;

const cssPx = (styles: CSSStyleDeclaration, token: string, fallback: number) =>
    parseFloat(styles.getPropertyValue(token)) || fallback;

// Shrinks the value font (from --font-size-text-metric-secondary down to --font-size-caption) until
// the text fits within MAX_LINES; below the minimum the CSS line-clamp adds the ellipsis. The size is
// written straight to the element, so the element stays the single source of truth.
export default function useShrinkToFit(text: string) {
    const ref = useRef<HTMLSpanElement>(null);

    useLayoutEffect(() => {
        const element = ref.current;
        if (!element) return;

        const styles = getComputedStyle(element);
        const maxFont = cssPx(styles, '--font-size-text-metric-secondary', 20);
        const minFont = cssPx(styles, '--font-size-caption', 12);

        const clamp = element.style.webkitLineClamp;
        element.style.webkitLineClamp = 'unset';

        for (let size = maxFont; size >= minFont; size -= 1) {
            element.style.fontSize = `${size}px`;
            const lineHeight = parseFloat(styles.lineHeight) || size;
            if (size === minFont || element.scrollHeight <= lineHeight * MAX_LINES + 1) break;
        }

        element.style.webkitLineClamp = clamp;
    }, [text]);

    return ref;
}
